'use client';

import useSWR from 'swr';
import { useUser } from '@clerk/clerk-react';

import { ApiError, notificationsApi } from '@/lib/api';
import type { NotificationTokenResponse } from '@/types/notifications';
import { useAuthedFetcher } from './swrConfig';

// Token lives 15 min server-side; refresh with margin so no surface ever holds an
// expired credential. Shared by the bell and the preferences section — one token, one
// timer, whatever mounts.
const TOKEN_REFRESH_MS = 12 * 60 * 1000;

export interface NotificationToken {
  /** True only once a token actually arrived — false while loading AND when the
   * environment has notifications unconfigured (404), so surfaces never flash in. */
  enabled: boolean;
  jwt: string | null;
  isLoading: boolean;
}

export function useNotificationToken(): NotificationToken {
  const authedFetch = useAuthedFetcher();
  const { user } = useUser();
  const userId = user?.id ?? null;

  const tokenQuery = useSWR<NotificationTokenResponse | null>(
    userId ? ['notifications-token', userId] : null,
    async () => {
      try {
        return await authedFetch((token) => notificationsApi.issueToken(token));
      } catch (err) {
        // 404 is the feature-off contract, not a failure — cache the "disabled" answer.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    {
      // A cached `null` means this environment answered 404 (notifications off) — stop
      // polling instead of re-asking a feature-off backend every cycle.
      refreshInterval: (latest) => (latest === null ? 0 : TOKEN_REFRESH_MS),
      // The inbox keeps polling in a background tab, so its short-lived credential must
      // refresh there too. Revalidation on focus remains a fallback for browser timer
      // throttling or a device that slept past the refresh interval.
      refreshWhenHidden: true,
      revalidateOnFocus: true,
      focusThrottleInterval: 5 * 60 * 1000,
    },
  );

  return {
    // Deliberately disabled on ANY error, including a failed background refresh with a
    // stale token still cached: that token expires within minutes of the missed refresh,
    // so hiding the surfaces beats letting them run into vendor 401s. The cost is a bell
    // that disappears until SWR's retry succeeds.
    enabled: Boolean(tokenQuery.data?.token) && !tokenQuery.error,
    jwt: tokenQuery.data?.token ?? null,
    isLoading: tokenQuery.isLoading,
  };
}
